import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckCircle2, XCircle, Clock, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export function AIAnalystExecutions() {
  // Fetch executions with auto-refresh
  const { data: executions, isLoading } = useQuery({
    queryKey: ["ai-analyst-executions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alpha_onchain_executions")
        .select("*")
        .order("executed_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data;
    },
    refetchInterval: 30000, // Auto-refresh every 30s
  });

  // Calculate stats
  const stats = executions
    ? {
        total: executions.length,
        success: executions.filter((e) => e.success).length,
        failed: executions.filter((e) => !e.success).length,
        successRate: executions.length > 0
          ? ((executions.filter((e) => e.success).length / executions.length) * 100).toFixed(1)
          : "0",
      }
    : { total: 0, success: 0, failed: 0, successRate: "0" };

  const getStatusBadge = (success: boolean) => {
    return success ? (
      <Badge variant="default" className="flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Sucesso
      </Badge>
    ) : (
      <Badge variant="destructive" className="flex items-center gap-1">
        <XCircle className="h-3 w-3" />
        Falha
      </Badge>
    );
  };

  const getReportTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      daily: "bg-blue-500/10 text-blue-500",
      weekly: "bg-purple-500/10 text-purple-500",
      preview: "bg-orange-500/10 text-orange-500",
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${colors[type] || "bg-gray-500/10 text-gray-500"}`}>
        {type}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Execuções</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sucesso</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{stats.success}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Falhas</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats.failed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.successRate}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Executions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Log de Execuções</CardTitle>
          <CardDescription>
            Histórico de todas as execuções (atualiza a cada 30 segundos)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : executions && executions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Coleta de Dados</TableHead>
                  <TableHead>Geração de Relatório</TableHead>
                  <TableHead>Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executions.map((execution) => (
                  <TableRow key={execution.id}>
                    <TableCell className="text-sm">
                      {execution.executed_at &&
                        format(new Date(execution.executed_at), "dd/MM HH:mm:ss", {
                          locale: ptBR,
                        })}
                    </TableCell>
                    <TableCell>{getReportTypeBadge(execution.report_type)}</TableCell>
                    <TableCell>{getStatusBadge(execution.success!)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {execution.data_collection || "N/A"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {execution.report_generation || "N/A"}
                    </TableCell>
                    <TableCell className="text-xs text-red-500 max-w-xs truncate">
                      {execution.error_message || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma execução encontrada
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
